import { ref, watch, computed } from 'vue';
import type { Ref } from 'vue';
import { useRoute } from 'vue-router';
import { DateTime } from 'luxon';
import type { DateRangeValue } from '@/utils/datePickerPresets';
import { calendarToJSDate, todayIn } from '@/utils/dt';
import { useTimezoneStore } from '@/stores/timezone';
import {
    getPayments,
    getPaymentTotals,
    type ListPaymentsParams,
} from '@/services/paymentService';
import type { PaymentStatusValue } from '@/services/paymentStatus';
import { formatCurrency } from '@/utils/formatUtils';
import {
    loadSavedPaymentBankIds,
    type PaymentBankSelection,
} from '@/composables/usePaymentBankOptions';
import {
    loadSavedPaymentTrustedAccountIds,
    type PaymentTrustedAccountSelection,
} from '@/composables/usePaymentTrustedAccountOptions';
// PHASE2: import { loadSavedPaymentBankAccountIds } from '@/composables/usePaymentBankAccountOptions';

export type PaymentsTabId = 'all' | 'deposits' | 'other';

export interface PaymentRow {
    id: number;
    transactionId: string;
    bank: string;
    trustedAccount: string;
    referenceNum: string;
    debtorName: string;
    accountName: string;
    accountNumber: string;
    ssn: string;
    coDebtorName: string;
    coDebtorSsn: string;
    amount: string;
    // PHASE2: destinationBankAccount: string;
    processDate: string;
    createdOn: string;
    status: string;
}

interface TabStat {
    id: string;
    label: string;
    value: string | number;
}

export const PAYMENTS_COLUMNS: { key: string; label: string }[] = [
    { key: 'transactionId', label: 'Transaction ID' },
    { key: 'bank', label: 'Bank' },
    { key: 'trustedAccount', label: 'Trust Account' },
    { key: 'accountName', label: 'Account Name' },
    { key: 'accountNumber', label: 'Account Number' },
    { key: 'referenceNum', label: 'Reference #' },
    { key: 'debtorName', label: 'Debtor Name' },
    { key: 'ssn', label: 'SSN' },
    { key: 'coDebtorName', label: 'Co-Debtor Name' },
    { key: 'coDebtorSsn', label: 'Co-Debtor SSN' },
    { key: 'amount', label: 'Amount' },
    // PHASE2: { key: 'destinationBankAccount', label: 'Destination Bank Account' },
    { key: 'processDate', label: 'Process Date' },
    { key: 'status', label: 'Status' },
];

export const PAYMENTS_LOCKED_COLUMN_KEYS = ['transactionId'];

const SORT_FIELD_MAP: Record<string, ListPaymentsParams['sort']> = {
    transactionId: 'trans_id',
    bank: 'bank',
    trustedAccount: 'trusted_account',
    referenceNum: 'reference',
    debtorName: 'debtor_name',
    accountName: 'account_name',
    coDebtorName: 'co_debtor_name',
    processDate: 'process_date',
};

export const PAYMENTS_SORTABLE_KEYS = new Set(Object.keys(SORT_FIELD_MAP));

/**
 * Reads a `YYYY-MM-DD` query param, rejecting anything that is not a real calendar date.
 * The URL is untrusted input, so a malformed value falls back to the default range
 * instead of reaching the API.
 */
function parseQueryDate(value: unknown): string | null {
    const raw = Array.isArray(value) ? value[0] : value;
    if (typeof raw !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
    const parsed = DateTime.fromISO(raw);
    return parsed.isValid && parsed.toISODate() === raw ? raw : null;
}

export function usePayments(
    statuses: Ref<PaymentStatusValue[] | undefined>,
    enableBankFilter = true,
) {
    const timezoneStore = useTimezoneStore();
    const route = useRoute();
    const rows = ref<PaymentRow[]>([]);
    const loading = ref(false);
    const totalsLoading = ref(false);
    const totalRecords = ref(0);
    const totalAmount = ref(0);
    const loadedTotalsFilterKey = ref<string | null>(null);
    const page = ref(1);
    const limit = ref(10);
    const search = ref('');
    const today = todayIn(timezoneStore.effectiveTimezone);
    const queryFrom = parseQueryDate(route.query.from);
    const queryTo = parseQueryDate(route.query.to);
    const linkedRange: [Date, Date] | null = queryFrom !== null && queryTo !== null
        && queryFrom <= queryTo
        ? [calendarToJSDate(queryFrom), calendarToJSDate(queryTo)]
        : null;
    const dateRange = ref<DateRangeValue>(
        linkedRange ?? [calendarToJSDate(today), calendarToJSDate(today)],
    );
    // PHASE1: Bank replaces Destination Bank Account as the Payments filter.

    const bankIds = ref<PaymentBankSelection>(linkedRange ? null : loadSavedPaymentBankIds());
    const payeeIds = ref<PaymentTrustedAccountSelection>(
        linkedRange ? null : loadSavedPaymentTrustedAccountIds(),
    );
    // PHASE2: const bankingAccountIds = ref<number[]>(loadSavedPaymentBankAccountIds());
    const sortField = ref<string>('processDate');
    const sortOrder = ref<1 | -1 | null>(-1);

    const filterKey = computed(() => {
        const [rangeStart, rangeEnd] = dateRange.value ?? [null, null];

        return JSON.stringify({
            search: search.value.trim(),
            bankIds: enableBankFilter ? bankIds.value : null,
            payeeIds: payeeIds.value,
            from: rangeStart ? DateTime.fromJSDate(rangeStart).toFormat('yyyy-MM-dd') : null,
            to: rangeEnd ? DateTime.fromJSDate(rangeEnd).toFormat('yyyy-MM-dd') : null,
            status: statuses.value ?? null,
        });
    });

    const hasExactTotals = computed(() => (
        !totalsLoading.value && loadedTotalsFilterKey.value === filterKey.value
    ));

    const stats = computed<TabStat[]>(() => [
        {
            id: 'total-transactions',
            label: 'Total Transactions',
            value: hasExactTotals.value ? totalRecords.value : '—',
        },
        {
            id: 'total-amount',
            label: 'Total Amount',
            value: hasExactTotals.value ? formatCurrency(totalAmount.value) : '—',
        },
    ]);

    function getApiSortOrder() {
        if (sortOrder.value === 1) return 'ASC';
        if (sortOrder.value === -1) return 'DESC';
        return undefined;
    }

    let latestRowsRequestId = 0;
    let inflightTotalsFilterKey: string | null = null;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    function hasNoSelectedBanks(): boolean {
        return enableBankFilter && bankIds.value !== null && bankIds.value.length === 0;
    }

    function hasNoSelectedTrustedAccounts(): boolean {
        return payeeIds.value !== null && payeeIds.value.length === 0;
    }

    function hasEmptyFilterSelection(): boolean {
        return hasNoSelectedBanks() || hasNoSelectedTrustedAccounts();
    }

    function clearPaymentsForEmptyFilterSelection() {
        // Invalidate a pending response before showing the intentional empty state.
        latestRowsRequestId += 1;
        inflightTotalsFilterKey = null;
        if (debounceTimer !== null) {
            clearTimeout(debounceTimer);
            debounceTimer = null;
        }

        rows.value = [];
        totalRecords.value = 0;
        totalAmount.value = 0;
        loadedTotalsFilterKey.value = filterKey.value;
        page.value = 1;
        loading.value = false;
        totalsLoading.value = false;
    }

    async function fetchPayments(fetchPage = page.value, fetchLimit = limit.value) {
        if (hasEmptyFilterSelection()) {
            clearPaymentsForEmptyFilterSelection();
            return;
        }

        const requestId = ++latestRowsRequestId;
        const currentFilterKey = filterKey.value;
        const needTotals = loadedTotalsFilterKey.value !== currentFilterKey
            && inflightTotalsFilterKey !== currentFilterKey;
        loading.value = true;
        page.value = fetchPage;
        limit.value = fetchLimit;
        if (needTotals) {
            totalsLoading.value = true;
            inflightTotalsFilterKey = currentFilterKey;
        }

        const [rangeStart, rangeEnd] = dateRange.value ?? [null, null];
        const apiSort = sortField.value ? SORT_FIELD_MAP[sortField.value] : undefined;
        const sharedParams: Omit<ListPaymentsParams, 'limit' | 'offset'> = {
            search: search.value.trim() || undefined,
            // PHASE1: Send selected deposit-bank IDs to the API.
            bankIds: (enableBankFilter && bankIds.value !== null)
                ? bankIds.value
                : undefined,
            payeeIds: payeeIds.value !== null ? payeeIds.value : undefined,
            from: rangeStart
                ? DateTime.fromJSDate(rangeStart).toFormat('yyyy-MM-dd')
                : undefined,
            to: rangeEnd
                ? DateTime.fromJSDate(rangeEnd).toFormat('yyyy-MM-dd')
                : undefined,
            status: statuses.value,
            // PHASE2: bankingAccountIds: bankingAccountIds.value.length > 0
            // PHASE2:     ? bankingAccountIds.value
            // PHASE2:     : undefined,
            sort: apiSort,
            order: getApiSortOrder(),
        };
        const { sort: _sort, order: _order, ...filterParams } = sharedParams;

        const rowsPromise = getPayments({
            ...sharedParams,
            limit: fetchLimit,
            offset: (fetchPage - 1) * fetchLimit,
        });
        const totalsPromise = needTotals ? getPaymentTotals(filterParams) : null;

        await Promise.all([
            rowsPromise.then((response) => {
                // Never let an older unfiltered request replace a newer filtered result.
                if (requestId !== latestRowsRequestId) return;

                rows.value = response.data.map((item) => ({
                    id: item.transId,
                    transactionId: String(item.transId),
                    bank: item.bank ?? '',
                    trustedAccount: item.payeeName ?? '',
                    referenceNum: item.reference ?? '',
                    debtorName: item.fullName ?? '',
                    accountName: item.accountName ?? '',
                    accountNumber: item.accountNumber ?? '',
                    ssn: item.ssn ?? '',
                    coDebtorName: item.coName ?? '',
                    coDebtorSsn: item.coSsn ?? '',
                    amount: item.amount !== null && item.amount !== undefined
                        ? formatCurrency(item.amount)
                        : '',
                    // PHASE2: destinationBankAccount: item.destinationBankAccount,
                    processDate: item.processDate ?? '',
                    status: item.tStatus === 'Cancel' ? 'Canceled' : item.tStatus ?? '',
                    createdOn: item.createdOn ?? '',
                }));

                page.value = fetchPage;
                limit.value = fetchLimit;
                if (loadedTotalsFilterKey.value !== currentFilterKey) {
                    const offset = (fetchPage - 1) * fetchLimit;
                    totalRecords.value = offset + response.data.length
                        + (response.data.length === fetchLimit ? 1 : 0);
                }
            }).finally(() => {
                if (requestId === latestRowsRequestId) {
                    loading.value = false;
                }
            }),
            totalsPromise === null
                ? Promise.resolve()
                : totalsPromise.then((totals) => {
                    if (filterKey.value !== currentFilterKey) return;
                    totalRecords.value = totals.total;
                    totalAmount.value = totals.totalAmount;
                    loadedTotalsFilterKey.value = currentFilterKey;
                }).catch(() => {
                    // Rows can still render; keep the pager estimate if totals time out.
                }).finally(() => {
                    if (inflightTotalsFilterKey === currentFilterKey) {
                        inflightTotalsFilterKey = null;
                    }
                    if (filterKey.value === currentFilterKey) {
                        totalsLoading.value = false;
                    }
                }),
        ]);
    }

    function scheduleRefetch() {
        if (hasEmptyFilterSelection()) {
            clearPaymentsForEmptyFilterSelection();
            return;
        }

        if (debounceTimer !== null) clearTimeout(debounceTimer);
        page.value = 1;
        debounceTimer = setTimeout(() => {
            debounceTimer = null;
            void fetchPayments(1, limit.value);
        }, 400);
    }

    watch(search, scheduleRefetch);
    // PHASE1: Bank selection applies only to All and Deposits.
    if (enableBankFilter) {
        watch(bankIds, scheduleRefetch, { deep: true });
    }
    watch(payeeIds, scheduleRefetch, { deep: true });
    watch(dateRange, scheduleRefetch, { deep: true });
    watch(statuses, scheduleRefetch, { deep: true });
    // PHASE2: watch(bankingAccountIds, scheduleRefetch, { deep: true });

    function getExportParams(): Omit<ListPaymentsParams, 'limit' | 'offset'> {
        return buildExportParamsFromState({
            statuses: statuses.value,
            enableBankFilter,
            search: search.value,
            bankIds: bankIds.value,
            payeeIds: payeeIds.value,
            dateRange: dateRange.value,
            sortField: sortField.value,
            sortOrder: sortOrder.value,
        });
    }

    return {
        rows,
        loading,
        totalsLoading,
        hasExactTotals,
        totalRecords,
        totalAmount,
        stats,
        search,
        bankIds,
        payeeIds,
        dateRange,
        // PHASE2: bankingAccountIds,
        page,
        limit,
        sortField,
        sortOrder,
        fetchPayments,
        getExportParams,
    };
}

function buildExportParamsFromState(options: {
    statuses?: PaymentStatusValue[];
    enableBankFilter: boolean;
    search: string;
    bankIds: PaymentBankSelection;
    payeeIds: PaymentTrustedAccountSelection;
    dateRange: DateRangeValue;
    sortField: string;
    sortOrder: 1 | -1 | null;
}): Omit<ListPaymentsParams, 'limit' | 'offset'> {
    const [rangeStart, rangeEnd] = options.dateRange ?? [null, null];
    const apiSort = options.sortField ? SORT_FIELD_MAP[options.sortField] : undefined;
    let order: ListPaymentsParams['order'];
    if (options.sortOrder === 1) order = 'ASC';
    else if (options.sortOrder === -1) order = 'DESC';

    return {
        search: options.search.trim() || undefined,
        bankIds: (options.enableBankFilter && options.bankIds !== null)
            ? options.bankIds
            : undefined,
        payeeIds: options.payeeIds !== null ? options.payeeIds : undefined,
        from: rangeStart
            ? DateTime.fromJSDate(rangeStart).toFormat('yyyy-MM-dd')
            : undefined,
        to: rangeEnd
            ? DateTime.fromJSDate(rangeEnd).toFormat('yyyy-MM-dd')
            : undefined,
        status: options.statuses,
        sort: apiSort,
        order,
    };
}
